import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import API_CONFIG from '../../config/api';
import {
  listAutomationKnowledgeProjections,
  projectAutomationKnowledgeJobToPkb,
  retractAutomationKnowledgeJobProjections,
} from './automationKnowledgeProjectionApi';
import { mapRepoConfigResponse } from './automationRepoConfigUtils';
import {
  AUTOMATION_KNOWLEDGE_ADVANCED_SELECTION_NOTE,
  AUTOMATION_KNOWLEDGE_APPROVE_BUTTON_LABEL,
  AUTOMATION_KNOWLEDGE_APPROVE_SUCCESS_MESSAGE,
  AUTOMATION_KNOWLEDGE_CAVEAT_CHECKBOX_LABEL,
  AUTOMATION_KNOWLEDGE_CAVEAT_TEXT,
  AUTOMATION_KNOWLEDGE_PROJECT_BUTTON_LABEL,
  AUTOMATION_KNOWLEDGE_PROJECTION_CAVEAT_CHECKBOX_LABEL,
  AUTOMATION_KNOWLEDGE_PROJECTION_CAVEAT_TEXT,
  AUTOMATION_KNOWLEDGE_PROJECTION_SUCCESS_MESSAGE,
  AUTOMATION_KNOWLEDGE_PULL_TITLE,
  AUTOMATION_KNOWLEDGE_RETRACT_BUTTON_LABEL,
  AUTOMATION_KNOWLEDGE_RETRACT_SUCCESS_MESSAGE,
  AUTOMATION_KNOWLEDGE_STAGED_NON_CONSUMER_NOTE,
  AUTOMATION_KNOWLEDGE_STAGED_STATUS_LABEL,
  candidateCountForJob,
  formatKnowledgeDateTime,
  isApproveEnabled,
  isProjectionSectionVisible,
  pickLatestCompletedGenerationJob,
  shortCandidateId,
  shortSha,
  summarizeProjectionStatuses,
} from './automationKnowledgePullUtils';

async function fetchJson(url, { method = 'GET', token, body } = {}) {
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.detail || payload.message || 'Request failed');
    error.status = response.status;
    throw error;
  }
  return payload;
}

export default function AutomationKnowledgePullSection({ projectId, disabled = false, onSuccess }) {
  const [repoConfig, setRepoConfig] = useState(null);
  const [generationJobs, setGenerationJobs] = useState([]);
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [projecting, setProjecting] = useState(false);
  const [retracting, setRetracting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [projectionMessage, setProjectionMessage] = useState(null);
  const [projectionError, setProjectionError] = useState(null);
  const [lastProjectionCounts, setLastProjectionCounts] = useState(null);
  const [caveatChecked, setCaveatChecked] = useState(false);
  const [projectionCaveatChecked, setProjectionCaveatChecked] = useState(false);
  const [projectionNote, setProjectionNote] = useState('');
  const [retractDialogOpen, setRetractDialogOpen] = useState(false);
  const [retractionReason, setRetractionReason] = useState('');

  const latestJob = useMemo(
    () => pickLatestCompletedGenerationJob(generationJobs, repoConfig?.repo_config_id || null),
    [generationJobs, repoConfig?.repo_config_id],
  );

  const candidateCount = useMemo(() => candidateCountForJob(latestJob), [latestJob]);
  const approveEnabled = useMemo(
    () => isApproveEnabled(latestJob) && caveatChecked && !disabled && !approving,
    [latestJob, caveatChecked, disabled, approving],
  );
  const projectionVisible = useMemo(
    () => isProjectionSectionVisible(latestJob, candidateCount),
    [latestJob, candidateCount],
  );
  const projectionSummary = useMemo(
    () => summarizeProjectionStatuses(projections, candidateCount),
    [projections, candidateCount],
  );
  const projectEnabled = useMemo(
    () => projectionVisible && projectionCaveatChecked && !disabled && !projecting && !retracting,
    [projectionVisible, projectionCaveatChecked, disabled, projecting, retracting],
  );
  const retractEnabled = useMemo(
    () => projectionVisible && !disabled && !projecting && !retracting,
    [projectionVisible, disabled, projecting, retracting],
  );
  const hasActiveProjection = projectionSummary.projected > 0;

  const loadProjections = useCallback(async (jobId, token) => {
    if (!projectId || !jobId || !token) {
      setProjections([]);
      return;
    }
    try {
      const response = await listAutomationKnowledgeProjections(
        projectId,
        { generationJobId: jobId },
        token,
      );
      setProjections(response.projections || []);
    } catch {
      setProjections([]);
    }
  }, [projectId]);

  const loadData = useCallback(async () => {
    if (!projectId) {
      setRepoConfig(null);
      setGenerationJobs([]);
      setProjections([]);
      return;
    }
    const token = localStorage.getItem('stratovai_access_token');
    if (!token) {
      setError('Authentication required.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const repoResponse = await fetchJson(
        `${API_CONFIG.BASE_URL}/automation/v1/repo-config?project_id=${encodeURIComponent(projectId)}`,
        { token },
      );
      const mappedRepo = mapRepoConfigResponse(repoResponse);
      setRepoConfig(mappedRepo);

      const jobsResponse = await fetchJson(
        `${API_CONFIG.BASE_URL}/automation/v1/knowledge/generation-jobs?project_id=${encodeURIComponent(projectId)}&limit=20`,
        { token },
      );
      const jobs = jobsResponse.jobs || [];
      setGenerationJobs(jobs);

      const latest = pickLatestCompletedGenerationJob(jobs, mappedRepo?.repo_config_id || null);
      if (latest?.approval_status === 'approved' && latest?.id) {
        await loadProjections(latest.id, token);
      } else {
        setProjections([]);
      }
    } catch (err) {
      setRepoConfig(null);
      setGenerationJobs([]);
      setProjections([]);
      setError(err?.message || 'Failed to load automation knowledge pull status.');
    } finally {
      setLoading(false);
    }
  }, [projectId, loadProjections]);

  useEffect(() => {
    setCaveatChecked(false);
    setProjectionCaveatChecked(false);
    setProjectionNote('');
    setSuccessMessage(null);
    setProjectionMessage(null);
    setProjectionError(null);
    setLastProjectionCounts(null);
    loadData();
  }, [loadData]);

  const handleApprove = async () => {
    if (!projectId || !latestJob || !approveEnabled) {
      return;
    }
    const token = localStorage.getItem('stratovai_access_token');
    if (!token) {
      setError('Authentication required.');
      return;
    }

    setApproving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await fetchJson(
        `${API_CONFIG.BASE_URL}/automation/v1/knowledge/generation-jobs/${encodeURIComponent(latestJob.id)}/approve?project_id=${encodeURIComponent(projectId)}`,
        {
          method: 'POST',
          token,
          body: {
            acknowledge_automation_caveat: true,
          },
        },
      );
      setSuccessMessage(AUTOMATION_KNOWLEDGE_APPROVE_SUCCESS_MESSAGE);
      setCaveatChecked(false);
      await loadData();
      if (onSuccess) {
        onSuccess(AUTOMATION_KNOWLEDGE_APPROVE_SUCCESS_MESSAGE);
      }
    } catch (err) {
      setError(err?.message || 'Failed to approve automation-derived knowledge.');
    } finally {
      setApproving(false);
    }
  };

  const handleProject = async () => {
    if (!projectId || !latestJob || !projectEnabled) {
      return;
    }
    const token = localStorage.getItem('stratovai_access_token');
    if (!token) {
      setProjectionError('Authentication required.');
      return;
    }

    setProjecting(true);
    setProjectionError(null);
    setProjectionMessage(null);
    try {
      const response = await projectAutomationKnowledgeJobToPkb(
        projectId,
        latestJob.id,
        {
          acknowledge_projection_caveat: true,
          projection_note: projectionNote.trim() || undefined,
        },
        token,
      );
      setLastProjectionCounts(response.counts || null);
      setProjectionMessage(AUTOMATION_KNOWLEDGE_PROJECTION_SUCCESS_MESSAGE);
      setProjectionCaveatChecked(false);
      setProjectionNote('');
      await loadData();
    } catch (err) {
      setProjectionError(err?.message || 'Failed to project automation-derived knowledge to staged PKB.');
    } finally {
      setProjecting(false);
    }
  };

  const handleRetract = async () => {
    if (!projectId || !latestJob || !retractEnabled) {
      return;
    }
    const token = localStorage.getItem('stratovai_access_token');
    if (!token) {
      setProjectionError('Authentication required.');
      return;
    }

    setRetracting(true);
    setProjectionError(null);
    setProjectionMessage(null);
    try {
      const response = await retractAutomationKnowledgeJobProjections(
        projectId,
        latestJob.id,
        {
          retraction_reason: retractionReason.trim() || undefined,
        },
        token,
      );
      setProjectionMessage(
        `${AUTOMATION_KNOWLEDGE_RETRACT_SUCCESS_MESSAGE} Retracted: ${response.retracted_count || 0}.`,
      );
      setRetractDialogOpen(false);
      setRetractionReason('');
      await loadData();
    } catch (err) {
      setProjectionError(err?.message || 'Failed to retract staged automation knowledge projection.');
    } finally {
      setRetracting(false);
    }
  };

  const repoLabel = repoConfig?.repo_owner && repoConfig?.repo_name
    ? `${repoConfig.repo_owner}/${repoConfig.repo_name}`
    : 'Repository not configured';

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }} data-testid="automation-knowledge-pull-section">
      <Typography variant="subtitle1" gutterBottom data-testid="automation-knowledge-pull-title">
        {AUTOMATION_KNOWLEDGE_PULL_TITLE}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review and approve automation-derived knowledge from the latest completed repository pull.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading automation knowledge pull status…</Typography>
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="automation-knowledge-pull-error">
          {error}
        </Alert>
      ) : null}

      {successMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="automation-knowledge-pull-success">
          {successMessage}
        </Alert>
      ) : null}

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Repository:</strong> {repoLabel}
        </Typography>
        {latestJob ? (
          <>
            <Typography variant="body2">
              <strong>Branch:</strong> {latestJob.branch || '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Commit:</strong> {shortSha(latestJob.commit_sha)}
            </Typography>
            <Typography variant="body2">
              <strong>Pulled:</strong> {formatKnowledgeDateTime(latestJob.completed_at || latestJob.queued_at)}
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong> {latestJob.status}
            </Typography>
            <Typography variant="body2">
              <strong>Files considered:</strong> {latestJob.counts?.files_considered ?? 0}
            </Typography>
            <Typography variant="body2">
              <strong>Candidates extracted:</strong> {candidateCount}
            </Typography>
            <Typography variant="body2">
              <strong>Failed/skipped:</strong>{' '}
              {(latestJob.counts?.candidates_failed || 0) + (latestJob.counts?.files_skipped || 0)}
            </Typography>
            <Box>
              <strong>Approval status:</strong>{' '}
              <Chip
                size="small"
                label={latestJob.approval_status || 'pending_approval'}
                color={latestJob.approval_status === 'approved' ? 'success' : 'default'}
                data-testid="automation-knowledge-pull-approval-status"
              />
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" data-testid="automation-knowledge-pull-empty">
            No completed automation knowledge pull is available yet.
          </Typography>
        )}
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }} data-testid="automation-knowledge-pull-caveat">
        {AUTOMATION_KNOWLEDGE_CAVEAT_TEXT}
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="automation-knowledge-pull-future-note">
        {AUTOMATION_KNOWLEDGE_ADVANCED_SELECTION_NOTE}
      </Typography>

      {latestJob?.approval_status !== 'approved' ? (
        <>
          <FormControlLabel
            control={(
              <Checkbox
                checked={caveatChecked}
                onChange={(event) => setCaveatChecked(event.target.checked)}
                disabled={!isApproveEnabled(latestJob) || disabled || approving}
                data-testid="automation-knowledge-pull-caveat-checkbox"
              />
            )}
            label={AUTOMATION_KNOWLEDGE_CAVEAT_CHECKBOX_LABEL}
            sx={{ mb: 2, display: 'block' }}
          />
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={!approveEnabled}
            data-testid="automation-knowledge-pull-approve-button"
          >
            {approving ? 'Approving…' : AUTOMATION_KNOWLEDGE_APPROVE_BUTTON_LABEL}
          </Button>
        </>
      ) : null}

      {projectionVisible ? (
        <Box sx={{ mt: 3 }} data-testid="automation-knowledge-projection-section">
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom data-testid="automation-knowledge-projection-title">
            Staged PKB projection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Project approved automation-derived knowledge into staged Product Knowledge. Staged entries remain contextual evidence only.
          </Typography>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Approved candidates:</strong> {candidateCount}
            </Typography>
            <Typography variant="body2" data-testid="automation-knowledge-projection-summary">
              <strong>Projection summary:</strong>{' '}
              projected {projectionSummary.projected}, not projected {projectionSummary.notProjected},
              failed {projectionSummary.failed}, retracted {projectionSummary.retracted}
              {(lastProjectionCounts?.candidates_deduped ?? 0) > 0
                ? `, deduped ${lastProjectionCounts.candidates_deduped}`
                : ''}
            </Typography>
            <Typography variant="body2" data-testid="automation-knowledge-staged-status">
              <strong>Staged PKB:</strong>{' '}
              {hasActiveProjection ? AUTOMATION_KNOWLEDGE_STAGED_STATUS_LABEL : 'Not projected yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="automation-knowledge-staged-non-consumer-note">
              {AUTOMATION_KNOWLEDGE_STAGED_NON_CONSUMER_NOTE}
            </Typography>
          </Stack>

          {projections.length > 0 ? (
            <Stack spacing={1} sx={{ mb: 2 }} data-testid="automation-knowledge-projection-list">
              {projections.map((row) => (
                <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">
                    <strong>Status:</strong> {row.projection_status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Candidate:</strong> {shortCandidateId(row.candidate_id)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>PKB entry:</strong> {row.pkb_entry_id ?? '—'}
                  </Typography>
                  {row.sanitized_path_label ? (
                    <Typography variant="body2">
                      <strong>Path label:</strong> {row.sanitized_path_label}
                    </Typography>
                  ) : null}
                  <Typography variant="body2">
                    <strong>Projected:</strong> {formatKnowledgeDateTime(row.projected_at)}
                  </Typography>
                  {row.retracted_at ? (
                    <Typography variant="body2">
                      <strong>Retracted:</strong> {formatKnowledgeDateTime(row.retracted_at)}
                    </Typography>
                  ) : null}
                </Paper>
              ))}
            </Stack>
          ) : null}

          {lastProjectionCounts ? (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="automation-knowledge-projection-counts">
              Last projection — considered: {lastProjectionCounts.candidates_considered ?? 0},
              projected: {lastProjectionCounts.candidates_projected ?? 0},
              deduped: {lastProjectionCounts.candidates_deduped ?? 0},
              failed: {lastProjectionCounts.candidates_failed ?? 0}
            </Alert>
          ) : null}

          {projectionError ? (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="automation-knowledge-projection-error">
              {projectionError}
            </Alert>
          ) : null}

          {projectionMessage ? (
            <Alert severity="success" sx={{ mb: 2 }} data-testid="automation-knowledge-projection-success">
              {projectionMessage}
            </Alert>
          ) : null}

          <Alert severity="warning" sx={{ mb: 2 }} data-testid="automation-knowledge-projection-caveat">
            {AUTOMATION_KNOWLEDGE_PROJECTION_CAVEAT_TEXT}
          </Alert>

          <FormControlLabel
            control={(
              <Checkbox
                checked={projectionCaveatChecked}
                onChange={(event) => setProjectionCaveatChecked(event.target.checked)}
                disabled={disabled || projecting || retracting}
                data-testid="automation-knowledge-projection-caveat-checkbox"
              />
            )}
            label={AUTOMATION_KNOWLEDGE_PROJECTION_CAVEAT_CHECKBOX_LABEL}
            sx={{ mb: 2, display: 'block' }}
          />

          <TextField
            label="Projection note (optional)"
            value={projectionNote}
            onChange={(event) => setProjectionNote(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={disabled || projecting || retracting}
            sx={{ mb: 2 }}
            data-testid="automation-knowledge-projection-note"
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="contained"
              onClick={handleProject}
              disabled={!projectEnabled}
              data-testid="automation-knowledge-project-button"
            >
              {projecting ? 'Projecting…' : AUTOMATION_KNOWLEDGE_PROJECT_BUTTON_LABEL}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setRetractDialogOpen(true)}
              disabled={!retractEnabled}
              data-testid="automation-knowledge-retract-button"
            >
              {retracting ? 'Retracting…' : AUTOMATION_KNOWLEDGE_RETRACT_BUTTON_LABEL}
            </Button>
          </Stack>
        </Box>
      ) : null}

      <Dialog
        open={retractDialogOpen}
        onClose={() => !retracting && setRetractDialogOpen(false)}
        data-testid="automation-knowledge-retract-dialog"
      >
        <DialogTitle>Retract staged projection?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This retracts staged automation knowledge projections for this pull and archives related staged PKB entries.
            Staged entries are not visible to AI Chat or Regression Coverage.
          </DialogContentText>
          <TextField
            label="Retraction reason (optional)"
            value={retractionReason}
            onChange={(event) => setRetractionReason(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            data-testid="automation-knowledge-retraction-reason"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetractDialogOpen(false)} disabled={retracting}>
            Cancel
          </Button>
          <Button onClick={handleRetract} color="warning" disabled={retracting} data-testid="automation-knowledge-retract-confirm">
            {retracting ? 'Retracting…' : AUTOMATION_KNOWLEDGE_RETRACT_BUTTON_LABEL}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 2 }} />
    </Paper>
  );
}
